import React, { useMemo } from 'react';
import { getIntl } from '../../../utils/intl';
import useCareTeamListMembers from '../../../hooks/api/useCareTeamMembers';
import CareTeamListMemberCard from '../../shared/CareTeamMemberCard';
import CareTeamListMemberTypeFilter, {
    CareTeamMemberTypeFilterValue,
    applyTypeFilter,
} from './CareTeamMemberTypeFilter';
import CareTeamMemberSearchFilter, { applySearchFilter } from './CareTeamMemberSearchFilter';
import CareTeamActiveFilter, { CareTeamMemberActiveFilterValue, applyActiveFilter } from './CareTeamActiveFilter';
import * as Filtering from '../../shared/Filtering';
import FilterButton from '../Filtering/FilterButton';
import HeaderContainer from '../../v2BaseComponents/HeaderContainer';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import { ContentStack } from '../../v2BaseComponents/Stack';
import MainContentContainer from '../../v2BaseComponents/MainContentContainer';
import EmptyStateHint from '../EmptyStateHint';
import { PRScrollView } from '../Scroll';

const intl = getIntl('careTeamMemberList', {
    add: 'Add a Care Team Member',
    filterTitle: 'Filter Care Team Members',
});

export type CareTeamListOwnProps = {
    profileId: string;
    enableAdd?: boolean;
    onSelect: (careTeamMemberId: string) => void;
    omittedCareTeamMemberIds?: string[];
    renderListStart?: () => React.ReactNode;
};

export type CareTeamListProps = CareTeamListOwnProps;

const CareTeamList: React.FC<CareTeamListProps> = (props) => {
    const { isLoadingCareTeamMembers, careTeamMembers } = useCareTeamListMembers({
        medicalProfileId: props.profileId,
    });
    const [CareTeamListMemberTypeFilterValue, setCareTeamListMemberTypeFilterValue] =
        React.useState<CareTeamMemberTypeFilterValue>('all');
    const [textSearchValue, setTextSearchValue] = React.useState<string>('');
    const [careTeamMemberStatusFilterValue, setCareTeamMemberStatusFilterValue] =
        React.useState<CareTeamMemberActiveFilterValue>('active');

    const maybeOmittedCareTeamMembers = useMemo(() => {
        if (!props.omittedCareTeamMemberIds) return careTeamMembers;

        return (careTeamMembers || []).filter((_) => !props.omittedCareTeamMemberIds?.includes(_.careTeamMemberId));
    }, [careTeamMembers, props.omittedCareTeamMemberIds]);

    const filteredCareTeamListMembers = applyActiveFilter(
        applySearchFilter(
            applyTypeFilter(maybeOmittedCareTeamMembers, CareTeamListMemberTypeFilterValue),
            textSearchValue,
        ),
        careTeamMemberStatusFilterValue,
    );

    return (
        <AnimateLoadedContent isLoading={isLoadingCareTeamMembers}>
            {() => (
                <>
                    <HeaderContainer>
                        <Filtering.Container
                            renderSearch={() => (
                                <CareTeamMemberSearchFilter
                                    value={textSearchValue}
                                    onChange={setTextSearchValue}
                                    isDisabled={!careTeamMembers.length}
                                />
                            )}
                            renderFilterButton={() => (
                                <FilterButton
                                    filterPromptTitle={intl('filterTitle')}
                                    onResetFilters={() => {
                                        setCareTeamListMemberTypeFilterValue('all');
                                        setTextSearchValue('');
                                        setCareTeamMemberStatusFilterValue('active');
                                    }}
                                    renderFilterContents={() => (
                                        <Filtering.FilterPopoverContent
                                            resetDisabled={
                                                CareTeamListMemberTypeFilterValue === 'all' &&
                                                textSearchValue === '' &&
                                                careTeamMemberStatusFilterValue === 'active'
                                            }
                                        >
                                            <CareTeamListMemberTypeFilter
                                                value={CareTeamListMemberTypeFilterValue}
                                                onChange={setCareTeamListMemberTypeFilterValue}
                                                allCareTeamMembers={careTeamMembers}
                                            />
                                            <CareTeamActiveFilter
                                                value={careTeamMemberStatusFilterValue}
                                                onChange={setCareTeamMemberStatusFilterValue}
                                                allCareTeamMembers={careTeamMembers}
                                            />
                                        </Filtering.FilterPopoverContent>
                                    )}
                                />
                            )}
                        />
                    </HeaderContainer>
                    <PRScrollView style={{ width: '100%', flex: 1 }}>
                        {
                            <MainContentContainer>
                                <ContentStack>
                                    {careTeamMembers.length ? (
                                        filteredCareTeamListMembers.map((careTeamMember) => (
                                            <CareTeamListMemberCard
                                                key={careTeamMember.careTeamMemberId}
                                                careTeamMember={careTeamMember}
                                                medicalProfileId={props.profileId}
                                                onPress={() => props.onSelect(careTeamMember.careTeamMemberId)}
                                            />
                                        ))
                                    ) : (
                                        <EmptyStateHint variant="care-team" />
                                    )}
                                </ContentStack>
                            </MainContentContainer>
                        }
                    </PRScrollView>
                </>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(CareTeamList);
